/**
 * Busca de registros do CRM (Task 13, brief 3.4).
 *
 * Endereço CANÔNICO (Task 13, limpeza de arquitetura): promovido de
 * `apps/plataforma/src/crm/search.ts` para `packages/crm`. A Edge Function
 * `supabase/functions/crm-runtime` e o app importam daqui; o arquivo antigo em
 * `apps/plataforma/src/crm/search.ts` reexporta deste módulo.
 *
 * **Reuso de `packages/admin-ops`, não reimplementação** (contrato de design
 * da Task 13): a pontuação é `rankSearchResults`, a MESMA que a busca
 * universal do command center e a `admin-runtime` usam. Um segundo
 * algoritmo de relevância faria o mesmo termo ordenar diferente em duas telas
 * do mesmo produto.
 *
 * Invariantes:
 *
 * 1. **Puro e sem I/O.** Recebe o dataset do CRM e devolve entradas pontuadas.
 * 2. **Nada é inventado no texto indexado.** Cada entrada aponta para um
 *    registro real (`id` do cliente, empresa, oportunidade, proposta ou
 *    tarefa) — é o que permite a busca navegar até o registro, e não até um
 *    resumo.
 * 3. **Termo curto não é "nada encontrado".** `rankSearchResults` exige termo
 *    com mais de um caractere; quando a consulta é curta demais, quem chama
 *    recebe `ignored: "query_too_short"` e DIZ isso, em vez de mostrar uma
 *    lista vazia que pareceria "o CRM não tem esse registro".
 */
import { rankSearchResults } from "../../admin-ops/src/index.js";
import { LEAD_STAGE_LABELS, QUOTE_STATUS_LABELS, TASK_KIND_LABELS } from "./pipeline.js";
/** Tipos de registro que a busca do CRM indexa (o `type` de `SearchEntry`). */
export const CRM_SEARCH_TYPES = ["customer", "company", "opportunity", "quote", "task"];
/**
 * Entradas buscáveis do CRM: um registro por linha, com o texto que o operador
 * digitaria (nome do cliente, título da negociação, número da proposta…).
 */
export function crmSearchEntries(dataset) {
    const entries = [];
    for (const customer of dataset.customers) {
        entries.push({
            type: "customer",
            id: customer.id,
            title: customer.name,
            subtitle: [customer.phone, customer.email].filter(Boolean).join(" ")
        });
    }
    for (const company of dataset.companies) {
        entries.push({
            type: "company",
            id: company.id,
            title: company.tradeName ?? company.legalName,
            subtitle: [company.legalName, company.taxId].filter(Boolean).join(" ")
        });
    }
    for (const opportunity of dataset.opportunities) {
        entries.push({
            type: "opportunity",
            id: opportunity.id,
            title: opportunity.title,
            subtitle: `${LEAD_STAGE_LABELS[opportunity.stage]} ${opportunity.ownerLabel} ${opportunity.customerId}`
        });
    }
    for (const quote of dataset.quotes) {
        entries.push({
            type: "quote",
            id: quote.id,
            title: quote.number,
            subtitle: `${QUOTE_STATUS_LABELS[quote.status]} ${quote.ownerLabel} ${quote.customerId}`
        });
    }
    for (const task of dataset.tasks) {
        entries.push({
            type: "task",
            id: task.id,
            title: task.title,
            subtitle: `${TASK_KIND_LABELS[task.kind]} ${task.ownerLabel} ${task.customerId}`
        });
    }
    return entries;
}
/**
 * Registros do CRM pontuados pela consulta, do mais relevante ao menos.
 *
 * `ignored` existe para a tela distinguir "busquei e não achei" de "não
 * busquei" — a mesma distinção que o resto da fase faz entre zero e fonte
 * ausente.
 */
export function searchCrm(query, dataset) {
    const terms = query.trim().split(/\s+/).filter((term) => term.length > 1);
    if (!terms.length)
        return { ignored: "query_too_short", entries: [] };
    return { ignored: null, entries: rankSearchResults(query, crmSearchEntries(dataset)) };
}
/** Ids de um tipo de registro, na ordem de relevância da busca. */
export function rankedCrmIds(query, dataset, type) {
    const result = searchCrm(query, dataset);
    return result.entries.filter((entry) => entry.type === type).map((entry) => entry.id);
}
